import type { MaybeComputedElementRef } from '@vueuse/core'
import { unrefElement } from '@vueuse/core'
import type { Nullable } from '~/types'
import type { MaybeRefOrGetter } from 'vue'
import { toValue } from 'vue'

/**
 * A composable that makes traditional pointer devices use their vertical scroll
 * as a horizontal scroll by hijacking the wheel event. This should be used
 * sparingly and only on containers where this behaviour is desired.
 *
 * It should be accompanied by a scrollbar hiding strategy in CSS, like the
 * Tailwind `scrollbar-hidden` utility.
 *
 * Pass a ref or computed that resolves to the element when side-scroll should
 * be active, or to null/undefined when it should be disabled. The listener is
 * attached and detached reactively when the value changes.
 *
 * @param elementRef The ref or computed that resolves to the element when side-scroll should be active, or to null/undefined when it should be disabled
 * @returns A promise that resolves when the side scroll is attached and detached
 */
export default function useSideScroll(elementRef: MaybeRefOrGetter<Nullable<MaybeComputedElementRef>>) {
  /**
   * The current element that the wheel event listener is attached to.
   */
  let currentElement: ReturnType<typeof unrefElement> = null

  /**
   * Handles the wheel event.
   * @param event - The wheel event.
   * @returns void
   */
  function handleWheel(event: WheelEvent) {
    const element = unrefElement(toValue(elementRef) ?? null)

    if (!element) {
      return
    }

    if (element.scrollWidth > element.clientWidth) {
      event.preventDefault()
      element.scrollLeft += (event.deltaY + event.deltaX)
    }
  }

  /**
   * Attaches the wheel event listener to the element.
   * @param el - The element to attach the wheel event listener to.
   */
  function attach(el: NonNullable<ReturnType<typeof unrefElement>>) {
    if (currentElement === el) {
      return
    }

    detach()

    currentElement = el
    currentElement.addEventListener('wheel', handleWheel as EventListener, { passive: false })
  }

  /**
   * Detaches the wheel event listener from the element.
   */
  function detach() {
    if (!currentElement) {
      return
    }

    currentElement.removeEventListener('wheel', handleWheel as EventListener)
    currentElement = null
  }

  /**
   * Watches the element ref and attaches/detaches the wheel event listener accordingly.
   */
  watchEffect(() => {
    const value = toValue(elementRef)
    const el = unrefElement(value ?? null)

    if (el) {
      attach(el)
    }
    else {
      detach()
    }
  })

  /**
   * Detaches the wheel event listener from the element when the component is unmounted.
   */
  onUnmounted(detach)
}
